import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../App";
import { Input } from "../components/ui/input";
import Layout from "../components/Layout";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { Save, FileText } from "lucide-react";

export default function UserProfile() {
  const { id } = useParams();
  const { axiosInstance } = useAuth();

  const [profile, setProfile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, [id]);

  const fetchProfile = async () => {
    try {
      const url = id ? `/users/${id}` : "/users/profile";
      const res = await axiosInstance.get(url);
      setProfile(res.data);
    } catch (err) {
      toast.error("Erreur chargement profil");
    }
  };

  const handleChange = (field, value) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      await axiosInstance.put(`/users/${profile._id}`, profile);
      toast.success("Profil mis à jour");
      fetchProfile();
    } catch (err) {
      toast.error("Erreur mise à jour");
    }
  };

  const labels = {
    identity_card: "Carte d'identité (Recto)",
    identity_card_back: "Carte d'identité (Verso)",
    cv: "CV",
    diplomas: "Diplôme 1",
    diplomas2: "Diplôme 2",
    certificat_bonne_moeurs: "Certificat bonne vie et mœurs",
    assurance_rc: "Assurance RC",
    visa_inami: "Visa / INAMI",
    document_independant: "Document indépendant",
  };



  const handleUploadPhoto = async (file) => {
    if (!file) return;

    console.log("PHOTO FILE:", file);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axiosInstance.post(
        `/users/upload-photo`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("UPLOAD OK:", res.data);

      toast.success("Photo mise à jour");
      fetchProfile();

    } catch (err) {
      console.log("UPLOAD ERROR:", err?.response?.data);
      toast.error("Erreur upload photo");
    }
  };

  const handleUploadDocument = async (key, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploading(true);

      await axiosInstance.post(
        `/users/${profile._id}/upload/${key}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      toast.success(`${key} uploadé`);
    } catch (err) {
      toast.error(`Erreur upload ${key}`);
    } finally {
      setUploading(false);
    }
  };

  if (!profile) return <div>Chargement...</div>;

  return (
    <Layout title="Profil utilisateur">
      <div className="p-6 grid gap-6 lg:grid-cols-2">

        {/* ================= PROFILE ================= */}
        <Card className="lg:col-span-2">
          <CardContent className="space-y-4 p-6">

            <h2 className="text-xl font-bold">Profil utilisateur</h2>

            {/* PHOTO */}
            <div>
              <Label>Photo de profil :</Label>
              <div className="flex items-center gap-4 mt-2">
                {profile.profile_photo && (
                  <img
                    src={profile.profile_photo}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                )}

                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleUploadPhoto(e.target.files?.[0])
                  }
                />
              </div>
            </div>

            <div>
              <Label>Prénom :</Label>
              <Input
                value={profile.first_name || ""}
                onChange={(e) => handleChange("first_name", e.target.value)}
              />
            </div>

            <div>
              <Label>Nom :</Label>
              <Input
                value={profile.last_name || ""}
                onChange={(e) => handleChange("last_name", e.target.value)}
              />
            </div>

            <div>
              <Label>Email :</Label>
              <Input
                value={profile.email || ""}
                onChange={(e) => handleChange("email", e.target.value)}
              />
            </div>

            <div>
              <Label>Téléphone :</Label>
              <Input
                value={profile.phone || ""}
                onChange={(e) => handleChange("phone", e.target.value)}
              />
            </div>

            <div>
              <Label>Adresse :</Label>
              <Input
                value={profile.address || ""}
                onChange={(e) => handleChange("address", e.target.value)}
              />
            </div>

            <div>
              <Label>Date de naissance :</Label>
              <Input
                type="date"
                value={profile.date_naissance || ""}
                onChange={(e) =>
                  handleChange("date_naissance", e.target.value)
                }
              />
            </div>

            <div>
              <Label>Numéro registre national :</Label>
              <Input
                value={profile.numero_registre_national || ""}
                onChange={(e) =>
                  handleChange("numero_registre_national", e.target.value)
                }
              />
            </div>

            <div>
              <Label>Visa / INAMI :</Label>
              <Input
                value={profile.visa_inami || ""}
                onChange={(e) => handleChange("visa_inami", e.target.value)}
              />
            </div>

            <div>
              <Label>IBAN :</Label>
              <Input
                value={profile.iban || ""}
                onChange={(e) => handleChange("iban", e.target.value)}
              />
            </div>

            <div>
              <Label>BIC :</Label>
              <Input
                value={profile.bic || ""}
                onChange={(e) => handleChange("bic", e.target.value)}
              />
            </div>

            <Button onClick={handleSave} className="w-full">
              <Save className="mr-2" size={18} />
              Enregistrer profil
            </Button>

          </CardContent>
        </Card>

        {/* ================= DOCUMENTS ================= */}
        <Card className="lg:col-span-2">
          <CardContent className="p-6 space-y-4">

            <h2 className="text-xl font-bold">Documents</h2>

            {[
              "cv",
              "diplomas",
              "diplomas2",
              "identity_card",
              "identity_card_back",
              "certificat_bonne_moeurs",
              "assurance_rc",
              "visa_inami",
              "document_independant",
            ].map((key) => (
              <div
                key={key}
                className="flex items-center justify-between border p-3 rounded"
              >
                <div className="flex items-center gap-2">
                  <FileText size={18} />
                  {/* <span>{key}</span> */}
                  {/* <span>{labels[key]}</span> */}
                  <span>{labels[key] || key}</span>
                </div>

                <div className="flex items-center gap-3">
                  {profile.documents?.[key]?.url && (
                    <a
                      href={profile.documents[key].url}
                      target="_blank"
                      className="text-blue-500 text-sm"
                    >
                      Voir
                    </a>
                  )}

                  <input
                    type="file"
                    onChange={(e) =>
                      handleUploadDocument(key, e.target.files?.[0])
                    }
                  />
                </div>
              </div>
            ))}

            {uploading && (
              <p className="text-sm text-gray-500">Upload en cours...</p>
            )}
          </CardContent>
        </Card>

      </div>
    </Layout>
  );
}